import { useListState } from "@/hooks/use-list-state";
import { useState, useCallback, useEffect, useRef } from "react";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Bell, Search, Lock, Wifi, WifiOff, BatteryMedium, CheckCircle2, Filter, Thermometer, Loader2, KeyRound, Users, BatteryLow, ArrowUp, ArrowDown, ArrowUpDown, X, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import type { SeamConnection, Vendor, Member, Property, LockBatteryNotification } from "@shared/schema";
import { sortProperties } from "@/lib/property-utils";
import { formatRelativeSync } from "@/lib/formatRelativeSync";
import { SmartLocksPropertyBrowser, LockDetailDrawer, type DrawerTab } from "@/pages/app-devices-smart-locks";
import type { UnifiedLock } from "@/pages/app-devices-smart-locks";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { JourneyEmptyState, useJourneyStage } from "@/lib/journey-empty-states";
import { usePageTitle } from "@/hooks/use-page-title";

type TabKey = "members" | "operations" | "smart_locks" | "thermostats";

interface SeamDevice {
  device_id: string;
  display_name: string;
  device_type: string;
  properties?: {
    online?: boolean;
    battery?: { level?: number };
    locked?: boolean;
    temperature?: number;
    current_climate_setting?: { hvac_mode_setting?: string };
    has_direct_power?: boolean;
  };
  location?: { location_name?: string };
}

interface SeamAccessCode {
  access_code_id: string;
  name?: string;
  code?: string;
  status?: string;
  starts_at?: string;
  ends_at?: string;
}

function formatDate(d?: string) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function LockAccessCodesForDevice({
  connectionId,
  device,
  search,
}: {
  connectionId: number;
  device: SeamDevice;
  search: string;
}) {
  const query = useQuery<SeamAccessCode[]>({
    queryKey: ["/api/app/devices/seam/access-codes", connectionId, device.device_id],
    queryFn: async () => {
      const res = await fetch(
        `/api/app/devices/seam/access-codes?connectionId=${connectionId}&deviceId=${device.device_id}`,
        { credentials: "include" }
      );
      if (!res.ok) return [];
      return res.json();
    },
  });
  const property = device.location?.location_name || "—";
  if (query.isLoading) return null;
  const codes = (query.data || []).filter((code) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      String(code.name ?? "").toLowerCase().includes(s) ||
      String(property ?? "").toLowerCase().includes(s) ||
      String(code.code ?? "").includes(s)
    );
  });
  return (
    <>
      {codes.map((code) => (
        <tr key={code.access_code_id} className="hover:bg-gray-50" data-testid={`row-member-${code.access_code_id}`}>
          <td className="px-4 py-3 font-medium text-gray-900">{code.name || "Guest"}</td>
          <td className="px-4 py-3 text-gray-600">{formatDate(code.starts_at)}</td>
          <td className="px-4 py-3 text-gray-600">{formatDate(code.ends_at)}</td>
          <td className="px-4 py-3">
            {code.code ? (
              <span className="bg-brand text-brand-foreground px-2 py-0.5 rounded text-xs font-mono">{code.code}</span>
            ) : (
              <span className="text-gray-400 text-xs">—</span>
            )}
          </td>
          <td className="px-4 py-3">
            {code.status === "set" ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <span className="text-gray-300">—</span>
            )}
          </td>
          <td className="px-4 py-3 text-gray-600">{property}</td>
        </tr>
      ))}
    </>
  );
}

function LockDevicesForConnection({
  connectionId,
  search,
}: {
  connectionId: number;
  search: string;
}) {
  const query = useQuery<SeamDevice[]>({
    queryKey: ["/api/app/devices/seam/locks", connectionId],
    queryFn: async () => {
      const res = await fetch(`/api/app/devices/seam/locks?connectionId=${connectionId}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });
  if (query.isLoading) return null;
  return (
    <>
      {(query.data || []).map((device) => (
        <LockAccessCodesForDevice key={device.device_id} connectionId={connectionId} device={device} search={search} />
      ))}
    </>
  );
}

export function VendorLockAccessDialog({
  vendor,
  properties,
  lockConnections,
  open,
  onOpenChange,
}: {
  vendor: Vendor;
  properties: Property[];
  lockConnections: SeamConnection[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("devices");
  type LockWithConnection = SeamDevice & { _connectionId: number };

  const { toast } = useToast();
  const vendorPhone = vendor.contactPhone || vendor.phone || "";
  const defaultCode = vendorPhone.replace(/\D/g, "").slice(-4);
  const [accessCode, setAccessCode] = useState(defaultCode);
  const [savedAccessCode, setSavedAccessCode] = useState(defaultCode);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const [selectedLocks, setSelectedLocks] = useState<Set<string>>(new Set());
  const [savedLocks, setSavedLocks] = useState<Set<string>>(new Set());
  const [lockStatuses, setLockStatuses] = useState<Record<string, "loading" | "success" | "error">>({});
  const [showConfirm, setShowConfirm] = useState(false);

  const vendorPropertyIds = (Array.isArray(vendor.propertyIds) ? vendor.propertyIds : []) as number[];
  const vendorProperties = sortProperties(properties.filter((p) => vendorPropertyIds.includes(p.id)));

  const selectedProperty = vendorProperties.find((p) => p.id === parseInt(selectedPropertyId, 10));

  const locksQuery = useQuery<LockWithConnection[]>({
    queryKey: ["/api/app/devices/seam/locks-for-property", selectedPropertyId, lockConnections.map((c) => c.id).join(",")],
    queryFn: async () => {
      if (!selectedPropertyId || lockConnections.length === 0) return [];
      const allLocks: LockWithConnection[] = [];
      for (const conn of lockConnections) {
        try {
          const res = await fetch(`/api/app/devices/seam/locks?connectionId=${conn.id}`, { credentials: "include" });
          if (res.ok) {
            const locks = await res.json() as SeamDevice[];
            allLocks.push(...locks.map((l) => ({ ...l, _connectionId: conn.id })));
          }
        } catch {}
      }
      const propName = selectedProperty?.name?.toLowerCase();
      if (!propName) return allLocks;
      return allLocks.filter((l) => {
        const locName = (l.location?.location_name || "").toLowerCase();
        if (!locName) return false;
        return locName.includes(propName) || propName.includes(locName);
      });
    },
    enabled: !!selectedPropertyId && lockConnections.length > 0,
  });

  const locks = locksQuery.data || [];
  const { handleItemClick: handleLockClick } = useRangeSelection(
    locks.map((l) => l.device_id),
    selectedLocks,
    setSelectedLocks,
  );

  const hasUnsavedChanges = useCallback(() => {
    if (accessCode !== savedAccessCode) return true;
    if (selectedLocks.size !== savedLocks.size) return true;
    for (const id of selectedLocks) {
      if (!savedLocks.has(id)) return true;
    }
    return false;
  }, [selectedLocks, savedLocks, accessCode, savedAccessCode]);

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowConfirm(true);
    } else {
      onOpenChange(false);
    }
  }, [hasUnsavedChanges, onOpenChange]);

  const handleToggleLock = (deviceId: string) => {
    setSelectedLocks((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
      }
      return next;
    });
  };

  const handleUpdateAccess = async () => {
    if (!accessCode || accessCode.length < 4) {
      toast({ title: "Invalid code", description: "Access code must be at least 4 digits.", variant: "destructive" });
      return;
    }

    const locksToUpdate = locks.filter((l) => selectedLocks.has(l.device_id));
    if (locksToUpdate.length === 0) {
      toast({ title: "No locks selected", description: "Select at least one lock to update access.", variant: "destructive" });
      return;
    }

    const newStatuses: Record<string, "loading" | "success" | "error"> = {};
    for (const lock of locksToUpdate) {
      newStatuses[lock.device_id] = "loading";
    }
    setLockStatuses({ ...newStatuses });

    for (const lock of locksToUpdate) {
      try {
        await apiRequest("POST", "/api/app/devices/seam/access-codes", {
          connectionId: lock._connectionId,
          deviceId: lock.device_id,
          name: `${vendor.name} Access`,
          code: accessCode,
        });
        setLockStatuses((prev) => ({ ...prev, [lock.device_id]: "success" }));
        toast({ title: "Access granted", description: `Code set on ${lock.display_name}` });
      } catch {
        setLockStatuses((prev) => ({ ...prev, [lock.device_id]: "error" }));
        toast({ title: "Failed", description: `Could not set code on ${lock.display_name}`, variant: "destructive" });
      }
    }

    setSavedLocks(new Set(selectedLocks));
    setSavedAccessCode(accessCode);
  };

  const handlePropertyChange = (val: string) => {
    setSelectedPropertyId(val);
    setSelectedLocks(new Set());
    setSavedLocks(new Set());
    setLockStatuses({});
  };

  const isUpdating = Object.values(lockStatuses).some((s) => s === "loading");

  return (
    <>
      <Dialog open={open} onOpenChange={(val) => { if (!val && isUpdating) return; if (!val) handleClose(); else onOpenChange(val); }}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="dialog-vendor-lock-access"
          onEscapeKeyDown={(e) => { if (isUpdating) e.preventDefault(); }}
          onInteractOutside={(e) => { if (isUpdating) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (isUpdating) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" data-testid="text-dialog-vendor-name">
              <KeyRound className="h-5 w-5 text-gray-500" />
              Lock Access — {vendor.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Access Code</label>
              <Input
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, ""))}
                placeholder="4+ digit code"
                maxLength={8}
                className="font-mono"
                data-testid="input-access-code"
              />
              <p className="text-xs text-gray-400 mt-1">Defaults to last 4 digits of vendor phone number</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Property</label>
              <Select value={selectedPropertyId} onValueChange={handlePropertyChange}>
                <SelectTrigger data-testid="select-property">
                  <SelectValue placeholder="Select a property" />
                </SelectTrigger>
                <SelectContent>
                  {vendorProperties.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-400">No properties assigned</div>
                  ) : (
                    vendorProperties.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)} data-testid={`option-property-${p.id}`}>
                        {p.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedPropertyId && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Smart Locks</label>
                {locksQuery.isLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-400 py-4" data-testid="text-locks-loading">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading locks...
                  </div>
                ) : locks.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2" data-testid="text-no-locks">No smart locks found for this property.</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {locks.map((lock) => (
                      <div
                        key={lock.device_id}
                        className="flex items-center justify-between p-2 border border-gray-200 rounded-lg"
                        data-testid={`row-lock-select-${lock.device_id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div onClick={(e) => { e.stopPropagation(); handleLockClick(lock.device_id, e.shiftKey); }}>
                            <Checkbox
                              checked={selectedLocks.has(lock.device_id)}
                              onCheckedChange={() => {}}
                              data-testid={`checkbox-lock-${lock.device_id}`}
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Lock className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-sm text-gray-800">{lock.display_name}</span>
                          </div>
                        </div>
                        <div>
                          {lockStatuses[lock.device_id] === "loading" && (
                            <Loader2 className="h-4 w-4 animate-spin text-gray-400" data-testid={`status-loading-${lock.device_id}`} />
                          )}
                          {lockStatuses[lock.device_id] === "success" && (
                            <CheckCircle2 className="h-4 w-4 text-green-500" data-testid={`status-success-${lock.device_id}`} />
                          )}
                          {lockStatuses[lock.device_id] === "error" && (
                            <span className="text-xs text-red-500 font-medium" data-testid={`status-error-${lock.device_id}`}>Failed</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              data-testid="button-save-close"
            >
              Save
            </Button>
            <Button
              onClick={handleUpdateAccess}
              disabled={readOnly || selectedLocks.size === 0 || !accessCode}
              data-testid="button-update-access"
            >
              Update Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent data-testid="dialog-unsaved-warning">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to leave without saving lock access?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-leave">Stay</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowConfirm(false); onOpenChange(false); }}
              data-testid="button-confirm-leave"
            >
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const VALID_TABS: TabKey[] = ["members", "operations", "smart_locks", "thermostats"];

export default function AppDevicesOverview() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  usePageTitle("Devices");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("devices");
  const [, navigate] = useLocation();
  const rawSearch = useSearch();
  const rawTab = new URLSearchParams(rawSearch).get("tab");
  const activeTab: TabKey = (VALID_TABS.includes(rawTab as TabKey) ? rawTab : "members") as TabKey;

  useEffect(() => {
    if (rawTab === "settings") {
      navigate("/app/devices/smart-locks?tab=settings");
    }
  }, [rawTab, navigate]);

  const setActiveTab = useCallback((tab: TabKey) => {
    navigate(`/app/devices?tab=${tab}`);
  }, [navigate]);
  const [search, setSearch] = useListState("devices-overview:search", "");
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

  type VendorSortCol = "name" | "category";
  const [vendorSort, setVendorSort] = useListState<VendorSortCol>("devices-overview:vendor-sort", "name");
  const [vendorSortDir, setVendorSortDir] = useListState<"asc" | "desc">("devices-overview:vendor-direction", "asc");
  const [filterCategories, setFilterCategories] = useListState<string[]>("devices-overview:categories", []);
  const [filterProperties, setFilterProperties] = useListState<number[]>("devices-overview:properties", []);
  const [vendorFilterOpen, setVendorFilterOpen] = useState(false);

  type ThermostatSortCol = "name" | "property" | "online" | "temperature";
  const [thermostatSort, setThermostatSort] = useListState<ThermostatSortCol>("devices-overview:thermostat-sort", "name");
  const [thermostatSortDir, setThermostatSortDir] = useListState<"asc" | "desc">("devices-overview:thermostat-direction", "asc");

  const connectionsQuery = useQuery<SeamConnection[]>({
    queryKey: ["/api/app/devices/connections"],
  });

  const lowBatteryQuery = useQuery<LockBatteryNotification[]>({
    queryKey: ["/api/app/devices/low-battery-notifications"],
    refetchInterval: 60_000,
  });
  const lowBatteryNotifications = lowBatteryQuery.data || [];

  const ttlockConnectionsQuery = useQuery<Array<{ id: number; ttlockUsername: string; status: string; lockCount?: number }>>({
    queryKey: ["/api/app/devices/ttlock/connections"],
  });

  const smartLockCountQuery = useQuery<{ total: number }>({
    queryKey: ["/api/app/devices/smart-locks/count"],
  });

  const checkedConnectionsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const lockConns = (connectionsQuery.data || []).filter((c) => c.deviceType === "smart_lock");
    const ttlockConns = ttlockConnectionsQuery.data || [];
    const seamToCheck = lockConns.filter((c) => !checkedConnectionsRef.current.has(`seam-${c.id}`));
    const ttlockToCheck = ttlockConns.filter(
      (c) => c.status === "connected" && !checkedConnectionsRef.current.has(`ttlock-${c.id}`)
    );
    if (seamToCheck.length === 0 && ttlockToCheck.length === 0) return;
    seamToCheck.forEach((c) => checkedConnectionsRef.current.add(`seam-${c.id}`));
    ttlockToCheck.forEach((c) => checkedConnectionsRef.current.add(`ttlock-${c.id}`));
    Promise.all([
      ...seamToCheck.map((c) =>
        fetch("/api/app/devices/seam/battery-check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ connectionId: c.id }),
        }).catch(() => null),
      ),
      ...ttlockToCheck.map((c) =>
        fetch("/api/app/devices/ttlock/battery-check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ connectionId: c.id }),
        }).catch(() => null),
      ),
    ]).then(() => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/low-battery-notifications"] });
    });
  }, [connectionsQuery.data, ttlockConnectionsQuery.data]);

  const vendorsQuery = useQuery<Vendor[]>({
    queryKey: ["/api/app/devices/vendors"],
  });

  const membersQuery = useQuery<Member[]>({
    queryKey: ["/api/app/devices/members"],
  });

  // Task #2256 — Members tab driven by this endpoint: active members only,
  // room-based passcode matching, real code window, room label.
  interface MemberLock {
    deviceId: string | null;
    pin: string | null;
    codeStart: string | null;
    codeEnd: string | null;
    codeName: string | null;
    codeId: string | null;
    lastUsedAt: string | null;
    candidates: Array<{
      codeId: string;
      codeName: string;
      pin: string;
      lastUsedAt: string | null;
    }>;
  }
  interface MembersAccessRow {
    memberId: number;
    name: string;
    moveInDate: string | null;
    moveOutDate: string | null;
    propertyId: number | null;
    propertyName: string | null;
    roomLabel: string | null;
    pin: string | null;
    codeStart: string | null;
    codeEnd: string | null;
    codeName: string | null;
    codeId: string | null;
    lastUsedAt: string | null;
    deviceId: string | null;
    candidates: Array<{
      codeId: string;
      codeName: string;
      pin: string;
      lastUsedAt: string | null;
    }>;
    // Per-lock breakdown (room-first). The flat fields above mirror locks[0]
    // for backward compatibility; older payloads omit `locks` entirely, in
    // which case the UI synthesizes a single lock from the flat fields.
    locks?: MemberLock[];
  }
  const membersAccessQuery = useQuery<MembersAccessRow[]>({
    queryKey: ["/api/app/devices/members-access"],
  });
  const codeOverrideMutation = useMutation({
    mutationFn: async ({ memberId, deviceId, codeId }: { memberId: number; deviceId: string | null; codeId: string | null }) => {
      if (codeId == null) {
        // Auto-match: clear only this lock's override when we know its deviceId
        // (multi-lock rooms); omit the param to clear all (legacy single-lock).
        const qs = deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : "";
        await apiRequest("DELETE", `/api/app/devices/members-access/${memberId}/code-override${qs}`);
      } else {
        // The codeId is opaque and encodes the lock, so the PUT route derives
        // the deviceId server-side — no need to send it in the body.
        await apiRequest("PUT", `/api/app/devices/members-access/${memberId}/code-override`, { codeId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/members-access"] });
    },
    onError: (err) => {
      toast({
        title: "Couldn't update code",
        description: parseErrorMessage(err, ""),
        variant: "destructive",
      });
    },
  });
  // Task #3502 — opt-in: push the pinned code's name to the physical lock so the
  // host's choice has a real-world effect (renames the on-lock passcode).
  const pushCodeMutation = useMutation({
    mutationFn: async ({ memberId, codeId }: { memberId: number; codeId: string }) => {
      await apiRequest("POST", `/api/app/devices/members-access/${memberId}/code-override/push`, { codeId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/devices/members-access"] });
      toast({
        title: "Code pushed to lock",
        description: "The lock passcode was renamed to the member's name.",
      });
    },
    onError: (err) => {
      toast({
        title: "Couldn't push code to lock",
        description: parseErrorMessage(err, ""),
        variant: "destructive",
      });
    },
  });
  const [membersDrawerDeviceId, setMembersDrawerDeviceId] = useState<string | null>(null);
  const [membersDrawerOpen, setMembersDrawerOpen] = useState(false);
  const [membersDrawerLock, setMembersDrawerLock] = useState<UnifiedLock | null>(null);
  const [membersDrawerInitialTab, setMembersDrawerInitialTab] = useState<DrawerTab>("access_codes");

  const lockInfoQuery = useQuery<UnifiedLock>({
    queryKey: ["/api/app/devices/locks/info", membersDrawerDeviceId],
    queryFn: async () => {
      const res = await fetch(
        `/api/app/devices/locks/info?deviceId=${encodeURIComponent(membersDrawerDeviceId!)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to fetch lock info");
      return res.json() as Promise<UnifiedLock>;
    },
    enabled: membersDrawerDeviceId != null,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (lockInfoQuery.data) setMembersDrawerLock(lockInfoQuery.data);
  }, [lockInfoQuery.data]);

  // Deep-link: /app/devices?tab=members&openLock=<deviceId>&lockTab=activity
  // opens the lock slide-out directly. Used by the inbox member-info "Locks"
  // row so clicking a member's lock jumps straight to its Activity log. The
  // one-shot params are stripped after opening so closing + re-rendering (or a
  // refresh) doesn't re-open the drawer.
  useEffect(() => {
    const params = new URLSearchParams(rawSearch);
    const openLock = params.get("openLock");
    if (!openLock) return;
    const lockTab = params.get("lockTab");
    setMembersDrawerDeviceId(openLock);
    setMembersDrawerInitialTab(
      lockTab === "activity" ? "activity" : lockTab === "manage" ? "manage" : "access_codes",
    );
    setMembersDrawerOpen(true);
    navigate("/app/devices?tab=members", { replace: true });
  }, [rawSearch, navigate]);

  type MembersSortCol = "name" | "property" | "codeStart" | "used" | "room";
  const [membersSort, setMembersSort] = useListState<MembersSortCol>("devices-overview:member-sort", "name");
  const [membersSortDir, setMembersSortDir] = useListState<"asc" | "desc">("devices-overview:member-direction", "asc");
  const [membersPropertyFilter, setMembersPropertyFilter] = useListState<Set<number>>("devices-overview:member-properties", new Set());
  const [membersFilterOpen, setMembersFilterOpen] = useState(false);

  const handleMembersSort = (col: MembersSortCol) => {
    if (membersSort === col) {
      setMembersSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setMembersSort(col);
      setMembersSortDir("asc");
    }
  };

  const MembersSortIcon = ({ col }: { col: MembersSortCol }) => {
    if (membersSort !== col) return <ArrowUpDown className="h-3 w-3 ml-1 inline text-gray-400" />;
    return membersSortDir === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1 inline text-brand" />
      : <ArrowDown className="h-3 w-3 ml-1 inline text-brand" />;
  };

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const lockConnections = (connectionsQuery.data || []).filter((c) => c.deviceType === "smart_lock");
  const thermostatConnections = (connectionsQuery.data || []).filter((c) => c.deviceType === "thermostat");

  // Use the /all endpoint as the single source of truth for thermostat
  // rows. It merges the latest stored polled reading (so "Temperature"
  // never shows null when we have any cached value) and the per-device
  // override (custom name + assigned PadPulse property). Sorting/filtering
  // below works against this canonical shape.
  interface ThermostatRow {
    deviceId: string;
    seamName: string;
    customName: string | null;
    propertyId: number | null;
    propertyIds: number[];
    provider: string;
    accountEmail: string | null;
    location: string | null;
    online: boolean;
    currentTemperatureF: number | null;
    hvacMode: string | null;
  }
  const thermostatsAllQuery = useQuery<ThermostatRow[]>({
    queryKey: ["/api/app/devices/seam/thermostats/all"],
    enabled: thermostatConnections.length > 0,
  });
  const allThermostats: ThermostatRow[] = thermostatsAllQuery.data || [];
  const thermostatsLoading = connectionsQuery.isLoading || (thermostatConnections.length > 0 && thermostatsAllQuery.isLoading);

  function thermostatDisplayName(t: ThermostatRow): string {
    return String(t.customName || t.seamName || "Unnamed device");
  }

  function handleThermostatSort(col: ThermostatSortCol) {
    if (thermostatSort === col) {
      setThermostatSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setThermostatSort(col);
      setThermostatSortDir("asc");
    }
  }

  function ThermostatSortIcon({ col }: { col: ThermostatSortCol }) {
    if (thermostatSort !== col) return <ArrowUpDown className="h-3 w-3 ml-1 inline text-gray-400" />;
    return thermostatSortDir === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1 inline text-brand" />
      : <ArrowDown className="h-3 w-3 ml-1 inline text-brand" />;
  }

  function handleVendorSort(col: VendorSortCol) {
    if (vendorSort === col) {
      setVendorSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setVendorSort(col);
      setVendorSortDir("asc");
    }
  }

  function VendorSortIcon({ col }: { col: VendorSortCol }) {
    if (vendorSort !== col) return <ArrowUpDown className="h-3 w-3 ml-1 inline text-gray-400" />;
    return vendorSortDir === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1 inline text-brand" />
      : <ArrowDown className="h-3 w-3 ml-1 inline text-brand" />;
  }

  function getVendorCategories(v: Vendor): string[] {
    const cats = new Set<string>();
    const serviceTypes = Array.isArray(v.serviceTypes) ? (v.serviceTypes as string[]) : [];
    const tags = Array.isArray(v.tags) ? (v.tags as string[]) : [];
    serviceTypes.forEach((s) => { if (s) cats.add(String(s)); });
    tags.forEach((t) => { if (t) cats.add(String(t)); });
    if (cats.size === 0 && v.category && v.category !== "other") cats.add(String(v.category));
    if (cats.size === 0 && v.category) cats.add(String(v.category));
    return Array.from(cats);
  }

  function getVendorPin(v: Vendor): string {
    const phone = (v.contactPhone || v.phone || "").replace(/\D/g, "");
    return phone.length >= 4 ? phone.slice(-4) : "";
  }

  const CATEGORY_COLORS: Record<string, string> = {
    cleaning: "bg-blue-100 text-blue-700",
    handyman: "bg-orange-100 text-orange-700",
    lawn: "bg-green-100 text-green-700",
    pest: "bg-yellow-100 text-yellow-700",
    plumbing: "bg-cyan-100 text-cyan-700",
    electrical: "bg-amber-100 text-amber-700",
    hvac: "bg-indigo-100 text-indigo-700",
    painting: "bg-pink-100 text-pink-700",
    landscaping: "bg-emerald-100 text-emerald-700",
    inspection: "bg-purple-100 text-purple-700",
    plumber: "bg-cyan-100 text-cyan-700",
    electrician: "bg-amber-100 text-amber-700",
    cleaner: "bg-blue-100 text-blue-700",
    landscaper: "bg-emerald-100 text-emerald-700",
    pest_control: "bg-yellow-100 text-yellow-700",
    painter: "bg-pink-100 text-pink-700",
  };

  // Provider-supplied location label from Seam (e.g. Nest/Ecobee location
  // name). Kept for potential future use but no longer drives the Property
  // column, which now resolves to the PadPulse property assignment.
  function thermostatLocationName(t: ThermostatRow): string {
    return t.location || "";
  }

  // allProperties / propertyMap must be declared BEFORE filteredSortedThermostats
  // because the thermostat filter IIFE calls thermostatPropertyName(), which reads
  // allProperties. Declaring them after the IIFE would put them in the temporal
  // dead zone and throw a ReferenceError the moment `search` is non-empty and
  // at least one thermostat is present (crashes any subtab, not just Thermostats).
  const allProperties = (propertiesQuery.data || []).filter((p) => p.status === "active");
  const propertyMap = new Map(allProperties.map((p) => [p.id, p.name]));

  // PadPulse property assignment for a thermostat — drives the Property
  // column, search, and sort so display/interaction stay consistent.
  function thermostatPropertyName(t: ThermostatRow): string {
    if (!t.propertyId) return "";
    const prop = allProperties.find((p) => p.id === t.propertyId);
    return String(prop?.name || prop?.address || `Property #${t.propertyId}`);
  }

  const filteredSortedThermostats = (() => {
    const s = search.toLowerCase();
    const filtered = allThermostats.filter((t) => {
      if (!search) return true;
      const name = thermostatDisplayName(t).toLowerCase();
      return name.includes(s) || thermostatPropertyName(t).toLowerCase().includes(s);
    });
    return filtered.slice().sort((a, b) => {
      const dir = thermostatSortDir === "asc" ? 1 : -1;
      if (thermostatSort === "name") {
        return dir * thermostatDisplayName(a).localeCompare(thermostatDisplayName(b));
      }
      if (thermostatSort === "property") {
        const la = thermostatPropertyName(a);
        const lb = thermostatPropertyName(b);
        if (!la && lb) return 1;
        if (la && !lb) return -1;
        return dir * la.localeCompare(lb);
      }
      if (thermostatSort === "online") {
        const oa = a.online ? 1 : 0;
        const ob = b.online ? 1 : 0;
        return dir * (ob - oa);
      }
      if (thermostatSort === "temperature") {
        const ta = a.currentTemperatureF;
        const tb = b.currentTemperatureF;
        if (ta == null && tb != null) return 1;
        if (ta != null && tb == null) return -1;
        if (ta == null && tb == null) return 0;
        return dir * ((ta as number) - (tb as number));
      }
      return 0;
    });
  })();

  const journeyStage = useJourneyStage();

  const allVendors = vendorsQuery.data || [];
  const allMembers = membersQuery.data || [];

  const allVendorCategories = (() => {
    const cats = new Set<string>();
    allVendors.forEach((v) => getVendorCategories(v).forEach((c) => cats.add(c)));
    return Array.from(cats).sort();
  })();

  const allVendorPropertyIds = (() => {
    const ids = new Set<number>();
    allVendors.forEach((v) => {
      (Array.isArray(v.propertyIds) ? (v.propertyIds as number[]) : []).forEach((id) => ids.add(id));
    });
    return Array.from(ids);
  })();

  const activeFilterCount = filterCategories.length + filterProperties.length;

  const filteredVendors = (() => {
    const s = search.toLowerCase();
    let result = allVendors.filter((v) => {
      if (s) {
        const vendorCats = getVendorCategories(v);
        const matchesSearch =
          String(v.name ?? "").toLowerCase().includes(s) ||
          vendorCats.some((c) => String(c ?? "").toLowerCase().includes(s)) ||
          String(v.contactName ?? "").toLowerCase().includes(s);
        if (!matchesSearch) return false;
      }
      if (filterCategories.length > 0) {
        const vendorCats = getVendorCategories(v);
        if (!filterCategories.some((fc) => vendorCats.includes(fc))) return false;
      }
      if (filterProperties.length > 0) {
        const vPids = Array.isArray(v.propertyIds) ? (v.propertyIds as number[]) : [];
        if (!filterProperties.some((fp) => vPids.includes(fp))) return false;
      }
      return true;
    });
    result = result.slice().sort((a, b) => {
      const dir = vendorSortDir === "asc" ? 1 : -1;
      if (vendorSort === "name") return dir * a.name.localeCompare(b.name);
      if (vendorSort === "category") {
        const ca = getVendorCategories(a)[0] || "";
        const cb = getVendorCategories(b)[0] || "";
        return dir * ca.localeCompare(cb);
      }
      return 0;
    });
    return result;
  })();

  const smartLockCount = smartLockCountQuery.isLoading ? undefined : (smartLockCountQuery.data?.total ?? 0);

  const allMembersAccess = membersAccessQuery.data || [];

  const tabs: { key: TabKey; label: string; count?: number; badge?: number }[] = [
    { key: "members", label: "Members", count: membersAccessQuery.isLoading ? undefined : allMembersAccess.length },
    { key: "operations", label: "Operations", count: allVendors.length },
    { key: "smart_locks", label: "Smart Locks", count: smartLockCount, badge: lowBatteryNotifications.length },
    { key: "thermostats", label: "Thermostats", count: thermostatsLoading ? undefined : allThermostats.length },
  ];

  return (
    <AppLayout>
      <div className="p-6">
        <ViewOnlyBanner area="devices" />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-tour="devices-heading">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your smart devices from PadPulse</p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="devices" />
            <GuideButton pageKey="devices" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="devices" />

        <div
          className={`border rounded-lg p-4 mb-6 ${
            lowBatteryNotifications.length > 0
              ? "border-red-200 bg-red-50"
              : "border-gray-200 bg-gray-50"
          }`}
          data-testid="panel-notifications"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Bell
                className={`h-4 w-4 ${
                  lowBatteryNotifications.length > 0 ? "text-red-500" : "text-gray-500"
                }`}
              />
              <span className="text-sm font-medium text-gray-700">Notifications</span>
              {lowBatteryNotifications.length > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold"
                  data-testid="badge-notifications-count"
                >
                  {lowBatteryNotifications.length}
                </span>
              )}
            </div>
            {lowBatteryNotifications.length > 0 && activeTab !== "smart_locks" && (
              <button
                type="button"
                onClick={() => { setActiveTab("smart_locks"); setSearch(""); }}
                className="text-xs font-medium text-red-600 hover:text-red-700"
                data-testid="link-view-low-battery-locks"
              >
                View locks
              </button>
            )}
          </div>
          {lowBatteryNotifications.length === 0 ? (
            <p className="text-sm text-gray-400">No notifications yet</p>
          ) : (
            <ul className="space-y-1.5">
              {lowBatteryNotifications.map((n) => {
                const label = n.deviceName || n.locationName || `Lock ${n.deviceId.slice(0, 8)}`;
                const where = n.locationName && n.deviceName ? n.locationName : null;
                // Task #1699 — Offline TTLock entries surface here the
                // same way low-battery alerts do; render the WifiOff
                // icon + "offline" copy when isOffline is true.
                const isOffline = n.isOffline === true;
                const Icon = isOffline ? WifiOff : BatteryLow;
                const testIdPrefix = isOffline ? "notification-offline" : "notification-low-battery";
                return (
                  <li
                    key={n.id}
                    className="flex items-start gap-2 text-sm text-red-700"
                    data-testid={`${testIdPrefix}-${n.deviceId}`}
                  >
                    <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <span className="font-medium">{label}</span>
                      {where && <span className="text-red-600/80"> · {where}</span>}
                      {isOffline && (
                        <span className="text-red-600/80"> · offline</span>
                      )}
                      {typeof n.batteryLevel === "number" && (
                        <span className="text-red-600/80"> · battery at {n.batteryLevel}%</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="border-b border-gray-200 mb-4">
          <div className="flex gap-0">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setSearch(""); }}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? "border-brand text-brand"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                data-testid={`tab-devices-${tab.key}`}
                data-tour={tab.key === "smart_locks" ? "devices-locks" : tab.key === "thermostats" ? "devices-thermostats" : tab.key === "members" ? "devices-members" : undefined}
              >
                <span className="inline-flex items-center gap-1.5">
                  {tab.count !== undefined ? `${tab.label} (${tab.count})` : tab.label}
                  {tab.badge ? (
                    <span
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold"
                      data-testid={`badge-tab-${tab.key}`}
                    >
                      {tab.badge}
                    </span>
                  ) : null}
                </span>
              </button>
            ))}
          </div>
        </div>

        {activeTab !== "smart_locks" && (
          <div className="flex items-center justify-between mb-4">
            {activeTab === "members" ? (
              <div className="flex items-center gap-2">
                <Popover open={membersFilterOpen} onOpenChange={setMembersFilterOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                      data-testid="button-filter"
                    >
                      <Filter className="h-3.5 w-3.5" />
                      {membersPropertyFilter.size > 0 ? `Property (${membersPropertyFilter.size})` : "+ Filter"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-64 p-2" data-testid="popover-members-filter">
                    <div className="text-xs font-medium text-gray-500 px-2 py-1.5 uppercase tracking-wider">Filter by Property</div>
                    <div className="max-h-64 overflow-y-auto">
                      {allProperties.length === 0 ? (
                        <div className="px-2 py-2 text-sm text-gray-400">No properties</div>
                      ) : allProperties.map((p) => {
                        const checked = membersPropertyFilter.has(p.id);
                        return (
                          <label
                            key={p.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                            data-testid={`filter-property-${p.id}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => {
                                setMembersPropertyFilter((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                                  return next;
                                });
                              }}
                              data-testid={`checkbox-filter-property-${p.id}`}
                            />
                            <span className="text-sm text-gray-700 truncate">{p.name}</span>
                          </label>
                        );
                      })}
                    </div>
                    {membersPropertyFilter.size > 0 && (
                      <button
                        type="button"
                        onClick={() => setMembersPropertyFilter(new Set())}
                        className="mt-2 w-full text-xs text-gray-500 hover:text-gray-700 py-1.5"
                        data-testid="button-clear-property-filter"
                      >
                        Clear filter
                      </button>
                    )}
                  </PopoverContent>
                </Popover>
                {membersPropertyFilter.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setMembersPropertyFilter(new Set())}
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                    data-testid="button-clear-filter-chip"
                  >
                    <X className="h-3 w-3" />
                    Clear
                  </button>
                )}
              </div>
            ) : activeTab === "operations" ? (
              <Popover open={vendorFilterOpen} onOpenChange={setVendorFilterOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="relative flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                    data-testid="button-filter"
                  >
                    <Filter className="h-3.5 w-3.5" />
                    + Filter
                    {activeFilterCount > 0 && (
                      <span
                        className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-semibold"
                        data-testid="badge-filter-count"
                      >
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-4" align="start" data-testid="popover-filter">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-800">Filters</span>
                      {activeFilterCount > 0 && (
                        <button
                          className="text-xs text-brand hover:underline"
                          onClick={() => { setFilterCategories([]); setFilterProperties([]); }}
                          data-testid="button-filter-clear"
                        >
                          Clear all
                        </button>
                      )}
                    </div>

                    {allVendorCategories.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Category</p>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {allVendorCategories.map((cat) => (
                            <label
                              key={cat}
                              className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:text-gray-900"
                              data-testid={`filter-category-${cat}`}
                            >
                              <Checkbox
                                checked={filterCategories.includes(cat)}
                                onCheckedChange={(checked) => {
                                  setFilterCategories((prev) =>
                                    checked ? [...prev, cat] : prev.filter((c) => c !== cat)
                                  );
                                }}
                              />
                              <span className="capitalize">{cat.replace(/_/g, " ")}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {allVendorPropertyIds.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Property</p>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {allVendorPropertyIds.map((pid) => {
                            const pName = propertyMap.get(pid) || `Property #${pid}`;
                            return (
                              <label
                                key={pid}
                                className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:text-gray-900"
                                data-testid={`filter-property-${pid}`}
                              >
                                <Checkbox
                                  checked={filterProperties.includes(pid)}
                                  onCheckedChange={(checked) => {
                                    setFilterProperties((prev) =>
                                      checked ? [...prev, pid] : prev.filter((p) => p !== pid)
                                    );
                                  }}
                                />
                                <span className="truncate">{pName}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {allVendorCategories.length === 0 && allVendorPropertyIds.length === 0 && (
                      <p className="text-sm text-gray-400 text-center py-2">No filter options available yet.</p>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700" data-testid="button-filter">
                <Filter className="h-3.5 w-3.5" />
                + Filter
              </button>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search..."
                className="pl-9 w-[280px] h-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="input-devices-search"
              />
            </div>
          </div>
        )}

        {activeTab === "members" && (() => {
          const s = search.trim().toLowerCase();
          const filtered = allMembersAccess.filter((r) => {
            if (membersPropertyFilter.size > 0) {
              if (r.propertyId == null || !membersPropertyFilter.has(r.propertyId)) return false;
            }
            if (!s) return true;
            return (
              String(r.name ?? "").toLowerCase().includes(s) ||
              String(r.propertyName ?? "").toLowerCase().includes(s) ||
              String(r.roomLabel ?? "").toLowerCase().includes(s) ||
              String(r.pin ?? "").toLowerCase().includes(s) ||
              (r.locks || []).some((lk) => String(lk.pin ?? "").toLowerCase().includes(s))
            );
          });
          const sorted = filtered.slice().sort((a, b) => {
            const dir = membersSortDir === "asc" ? 1 : -1;
            if (membersSort === "name") {
              return dir * (a.name || "").localeCompare(b.name || "");
            }
            if (membersSort === "codeStart") {
              const ta = a.codeStart ? new Date(a.codeStart).getTime() : null;
              const tb = b.codeStart ? new Date(b.codeStart).getTime() : null;
              if (ta == null && tb == null) return 0;
              if (ta == null) return 1;
              if (tb == null) return -1;
              return dir * (ta - tb);
            }
            if (membersSort === "used") {
              const ta = a.lastUsedAt ? new Date(a.lastUsedAt).getTime() : null;
              const tb = b.lastUsedAt ? new Date(b.lastUsedAt).getTime() : null;
              if (ta == null && tb == null) return 0;
              if (ta == null) return 1;
              if (tb == null) return -1;
              return dir * (ta - tb);
            }
            if (membersSort === "room") {
              const ra = a.roomLabel || "";
              const rb = b.roomLabel || "";
              if (!ra && rb) return 1;
              if (ra && !rb) return -1;
              return dir * ra.localeCompare(rb);
            }
            const la = a.propertyName || "";
            const lb = b.propertyName || "";
            if (!la && lb) return 1;
            if (la && !lb) return -1;
            return dir * la.localeCompare(lb);
          });
          return (
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        className="flex items-center gap-0 hover:text-gray-800 focus:outline-none uppercase"
                        onClick={() => handleMembersSort("name")}
                        data-testid="button-sort-members-name"
                      >
                        Name<MembersSortIcon col="name" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        className="flex items-center gap-0 hover:text-gray-800 focus:outline-none uppercase"
                        onClick={() => handleMembersSort("property")}
                        data-testid="button-sort-members-property"
                      >
                        Property<MembersSortIcon col="property" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        className="flex items-center gap-0 hover:text-gray-800 focus:outline-none uppercase"
                        onClick={() => handleMembersSort("room")}
                        data-testid="button-sort-members-room"
                      >
                        Room<MembersSortIcon col="room" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        className="flex items-center gap-0 hover:text-gray-800 focus:outline-none uppercase"
                        onClick={() => handleMembersSort("codeStart")}
                        data-testid="button-sort-members-code-start"
                      >
                        Code Start<MembersSortIcon col="codeStart" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Code Name</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">PIN</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        className="flex items-center gap-0 hover:text-gray-800 focus:outline-none uppercase"
                        onClick={() => handleMembersSort("used")}
                        data-testid="button-sort-members-used"
                      >
                        Last Used<MembersSortIcon col="used" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {membersAccessQuery.isLoading ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">Loading...</td></tr>
                  ) : allMembersAccess.length === 0 && lockConnections.length === 0 && (ttlockConnectionsQuery.data || []).length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No smart locks connected. Go to Smart Locks to connect a device account to view member access codes.</td></tr>
                  ) : allMembersAccess.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No active members yet.</td></tr>
                  ) : sorted.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No members match your filters.</td></tr>
                  ) : sorted.flatMap((r) => {
                    // One row per lock the member can open (multi-lock rooms,
                    // e.g. interior door + private entrance). Older payloads
                    // omit `locks`, so synthesize a single lock from the flat
                    // fields. Member name / property / room are shown once and
                    // rowSpan the member's lock rows.
                    const lockRows: MemberLock[] =
                      r.locks && r.locks.length > 0
                        ? r.locks
                        : [
                            {
                              deviceId: r.deviceId,
                              pin: r.pin,
                              codeStart: r.codeStart,
                              codeEnd: r.codeEnd,
                              codeName: r.codeName,
                              codeId: r.codeId,
                              lastUsedAt: r.lastUsedAt,
                              candidates: r.candidates,
                            },
                          ];
                    const multi = lockRows.length > 1;
                    return lockRows.map((lk, i) => {
                      const suffix = multi ? `-${i}` : "";
                      const idBase = `${r.memberId}${suffix}`;
                      const first = i === 0;
                      return (
                        <tr
                          key={`${r.memberId}-${lk.deviceId ?? i}`}
                          className={`hover:bg-gray-50 ${lk.deviceId ? "cursor-pointer" : ""}`}
                          data-testid={`row-member-${idBase}`}
                          onClick={() => {
                            if (lk.deviceId) {
                              setMembersDrawerDeviceId(lk.deviceId);
                              setMembersDrawerInitialTab("access_codes");
                              setMembersDrawerOpen(true);
                            }
                          }}
                        >
                          {first && (
                            <td className="px-4 py-3 font-medium align-top" rowSpan={lockRows.length} data-testid={`text-member-name-${r.memberId}`}>
                              <Link
                                href={`/app/members/${r.memberId}`}
                                className="text-brand hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded"
                                data-testid={`link-member-lock-${r.memberId}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {r.name}
                              </Link>
                            </td>
                          )}
                          {first && (
                            <td className="px-4 py-3 text-gray-600 align-top" rowSpan={lockRows.length} data-testid={`text-member-property-${r.memberId}`}>
                              {r.propertyName || <span className="text-gray-400">—</span>}
                            </td>
                          )}
                          {first && (
                            <td className="px-4 py-3 text-gray-600 text-xs align-top" rowSpan={lockRows.length} data-testid={`text-member-room-${r.memberId}`}>
                              {r.roomLabel || <span className="text-gray-400">—</span>}
                            </td>
                          )}
                          <td className="px-4 py-3 text-gray-600">{lk.codeStart ? formatDate(lk.codeStart) : <span className="text-gray-400">—</span>}</td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            {lk.candidates.length > 0 ? (
                              <div className="flex items-center gap-1">
                                <Select
                                  value={lk.codeId ?? "__auto__"}
                                  onValueChange={(val) => {
                                    codeOverrideMutation.mutate({
                                      memberId: r.memberId,
                                      deviceId: lk.deviceId,
                                      codeId: val === "__auto__" ? null : val,
                                    });
                                  }}
                                >
                                  <SelectTrigger
                                    className="h-8 w-[180px] text-xs"
                                    data-testid={`select-member-code-${idBase}`}
                                  >
                                    <SelectValue placeholder="Auto-match" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__auto__" data-testid={`option-member-code-auto-${idBase}`}>
                                      Auto-match
                                    </SelectItem>
                                    {lk.candidates.map((c) => (
                                      <SelectItem
                                        key={c.codeId}
                                        value={c.codeId}
                                        data-testid={`option-member-code-${idBase}-${c.codeId}`}
                                      >
                                        {c.codeName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {lk.codeId && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 shrink-0 text-gray-500 hover:text-brand"
                                    title="Change code name to member name"
                                    aria-label="Change code name to member name"
                                    disabled={pushCodeMutation.isPending}
                                    onClick={() =>
                                      pushCodeMutation.mutate({ memberId: r.memberId, codeId: lk.codeId! })
                                    }
                                    data-testid={`button-push-member-code-${idBase}`}
                                  >
                                    {pushCodeMutation.isPending && pushCodeMutation.variables?.codeId === lk.codeId ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Upload className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs" data-testid={`text-member-code-name-${idBase}`}>—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {lk.pin ? (
                              <span
                                className={`${r.moveOutDate ? "bg-destructive text-destructive-foreground" : "bg-brand text-brand-foreground"} px-2 py-0.5 rounded text-xs font-mono`}
                                data-testid={`text-member-pin-${idBase}`}
                              >
                                {lk.pin}
                              </span>
                            ) : (
                              <span
                                className="text-gray-400 text-xs"
                                data-testid={`text-member-pin-${idBase}`}
                              >
                                —
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600" data-testid={`text-member-used-${idBase}`}>
                            {(() => {
                              const relUsed = formatRelativeSync(lk.lastUsedAt);
                              return relUsed ? (
                                <span title={formatDate(lk.lastUsedAt!)}>{relUsed}</span>
                              ) : (
                                <span className="text-gray-400">Never</span>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}

        {activeTab === "operations" && (
          <>
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        className="flex items-center gap-0 hover:text-gray-800 focus:outline-none"
                        onClick={() => handleVendorSort("name")}
                        data-testid="button-sort-vendors-name"
                      >
                        Vendor Name<VendorSortIcon col="name" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button
                        className="flex items-center gap-0 hover:text-gray-800 focus:outline-none"
                        onClick={() => handleVendorSort("category")}
                        data-testid="button-sort-vendors-category"
                      >
                        Category<VendorSortIcon col="category" />
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Properties</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">PIN</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {vendorsQuery.isLoading ? (
                    <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">Loading...</td></tr>
                  ) : filteredVendors.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={(search || activeFilterCount > 0) ? "px-4 py-8 text-center text-sm text-gray-400" : "px-4 py-6"}>
                        {(search || activeFilterCount > 0) ? (
                          "No vendors match your search or filters."
                        ) : (
                          <JourneyEmptyState
                            page="vendors"
                            stage={journeyStage}
                            icon={<Users className="h-10 w-10 text-gray-300" />}
                          />
                        )}
                      </td>
                    </tr>
                  ) : filteredVendors.map((v) => {
                    const vendorCats = getVendorCategories(v);
                    const vendorPin = getVendorPin(v);
                    const propIds = Array.isArray(v.propertyIds) ? (v.propertyIds as number[]) : [];
                    const propNames = propIds.map((pid) => propertyMap.get(pid) || `Property #${pid}`);
                    return (
                      <tr
                        key={v.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedVendor(v)}
                        data-testid={`row-vendor-${v.id}`}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900" data-testid={`text-vendor-name-${v.id}`}>{v.name}</td>
                        <td className="px-4 py-3" data-testid={`text-vendor-category-${v.id}`}>
                          {vendorCats.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {vendorCats.map((cat) => (
                                <span
                                  key={cat}
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${CATEGORY_COLORS[cat] || "bg-gray-100 text-gray-600"}`}
                                  data-testid={`pill-category-${v.id}-${cat}`}
                                >
                                  {cat.replace(/_/g, " ")}
                                </span>
                              ))}
                            </div>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600" data-testid={`text-vendor-properties-${v.id}`}>
                          {propNames.length > 0 ? (
                            <HoverCard openDelay={200} closeDelay={100}>
                              <HoverCardTrigger asChild>
                                <button
                                  className="text-brand hover:underline font-medium tabular-nums"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`button-vendor-properties-count-${v.id}`}
                                >
                                  {propNames.length} {propNames.length === 1 ? "property" : "properties"}
                                </button>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-56 p-3" align="start" data-testid={`popover-vendor-properties-${v.id}`}>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Assigned Properties</p>
                                <ul className="space-y-1">
                                  {propNames.map((name) => (
                                    <li key={name} className="text-sm text-gray-700">{name}</li>
                                  ))}
                                </ul>
                              </HoverCardContent>
                            </HoverCard>
                          ) : "—"}
                        </td>
                        <td className="px-4 py-3" data-testid={`text-vendor-pin-${v.id}`}>
                          {vendorPin ? (
                            <span className="bg-brand text-brand-foreground px-2 py-0.5 rounded text-xs font-mono">{vendorPin}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {selectedVendor && (
              <VendorLockAccessDialog
                vendor={selectedVendor}
                properties={allProperties}
                lockConnections={lockConnections}
                open={!!selectedVendor}
                onOpenChange={(val) => { if (!val) setSelectedVendor(null); }}
              />
            )}
          </>
        )}

        {activeTab === "smart_locks" && (
          <div className="space-y-3">
            <div
              className="flex items-center justify-between rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 px-4 py-2.5 text-sm"
              data-testid="callout-smart-locks-connect-more"
            >
              <span className="text-blue-900 dark:text-blue-200">
                <span className="font-semibold">Looking to connect more Smart Locks?</span>
              </span>
              <Link
                href="/app/devices/smart-locks"
                className="ml-3 inline-flex items-center text-sm font-medium text-brand hover:text-brand-hover underline"
                data-testid="link-go-to-smart-locks-page"
              >
                Go to Smart Locks →
              </Link>
            </div>
            <SmartLocksPropertyBrowser />
          </div>
        )}

        {activeTab === "thermostats" && (
          <div className="space-y-3">
            <div
              className="flex items-center justify-between rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 px-4 py-2.5 text-sm"
              data-testid="callout-thermostats-connect-more"
            >
              <span className="text-blue-900 dark:text-blue-200">
                <span className="font-semibold">Looking to connect more Thermostats?</span>
              </span>
              <Link
                href="/app/devices/thermostats"
                className="ml-3 inline-flex items-center text-sm font-medium text-brand hover:text-brand-hover underline"
                data-testid="link-go-to-thermostats-connect-page"
              >
                Go to Thermostats →
              </Link>
            </div>
            {thermostatConnections.length > 0 && (
              <div
                className="flex items-center justify-between rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 px-4 py-2.5 text-sm"
                data-testid="callout-thermostats-adjust"
              >
                <span className="text-blue-900 dark:text-blue-200">
                  <span className="font-semibold">Need to make adjustments?</span>{" "}
                  <span className="text-blue-700 dark:text-blue-300">Rename devices, assign them to properties, and manage presets on the Thermostats page.</span>
                </span>
                <Link
                  href="/app/devices/thermostats?subtab=devices"
                  className="ml-3 inline-flex items-center text-sm font-medium text-brand hover:text-brand-hover underline"
                  data-testid="link-go-to-thermostats-page"
                >
                  Go to Thermostats →
                </Link>
              </div>
            )}
          <div className="border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-0 hover:text-gray-800 focus:outline-none"
                      onClick={() => handleThermostatSort("name")}
                      data-testid="button-sort-thermostats-name"
                    >
                      Name<ThermostatSortIcon col="name" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-0 hover:text-gray-800 focus:outline-none"
                      onClick={() => handleThermostatSort("property")}
                      data-testid="button-sort-thermostats-property"
                    >
                      Property<ThermostatSortIcon col="property" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-0 hover:text-gray-800 focus:outline-none"
                      onClick={() => handleThermostatSort("online")}
                      data-testid="button-sort-thermostats-online"
                    >
                      Online<ThermostatSortIcon col="online" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      className="flex items-center gap-0 hover:text-gray-800 focus:outline-none"
                      onClick={() => handleThermostatSort("temperature")}
                      data-testid="button-sort-thermostats-temperature"
                    >
                      Temperature<ThermostatSortIcon col="temperature" />
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {thermostatsLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">Loading...</td></tr>
                ) : thermostatConnections.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6">
                      <JourneyEmptyState
                        page="devices_thermostats"
                        stage={journeyStage}
                        icon={<Thermometer className="h-10 w-10 text-gray-300" />}
                      />
                    </td>
                  </tr>
                ) : filteredSortedThermostats.map((t) => (
                  <tr key={t.deviceId} className="hover:bg-gray-50" data-testid={`row-thermostat-${t.deviceId}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-medium text-gray-900">
                        <Thermometer className="h-4 w-4 text-gray-400" />
                        {thermostatDisplayName(t)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{thermostatPropertyName(t) || "—"}</td>
                    <td className="px-4 py-3">
                      {t.online ? (
                        <Wifi className="h-4 w-4 text-green-500" />
                      ) : (
                        <Wifi className="h-4 w-4 text-gray-300" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 tabular-nums">
                      {t.currentTemperatureF != null ? `${Math.round(t.currentTemperatureF)}°F` : "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 capitalize">
                      {t.hvacMode || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        )}

      </div>

      <LockDetailDrawer
        selectedLock={membersDrawerLock}
        open={membersDrawerOpen}
        initialTab={membersDrawerInitialTab}
        onOpenChange={(open) => {
          setMembersDrawerOpen(open);
          if (!open) {
            setMembersDrawerDeviceId(null);
            setMembersDrawerLock(null);
            setMembersDrawerInitialTab("access_codes");
          }
        }}
        readOnly={readOnly}
        selectedPropertyId={null}
      />
    </AppLayout>
  );
}
